import { ExtractedDocumentData, ExtractedField, ValidatedData, FieldChange } from '../types';

/**
 * API payload interfaces for different submission endpoints
 */
export interface AugmentumPayload {
  documentId: string;
  documentType: string;
  validatedFields: {
    grantor?: string;
    grantee?: string;
    parcelNumber?: string;
    considerationAmount?: string;
    ownershipType?: string;
    legalDescription?: string;
    recordingDate?: string;
    documentId?: string;
    documentSubtype?: string;
    granteeMailingAddress?: string;
    saleDate?: string;
    [key: string]: string | undefined;
  };
  metadata: {
    validationTimestamp: string;
    validatedBy: string;
    originalConfidenceScores: Record<string, number>;
    changesSummary: FieldChange[];
  };
}

export interface ValidationServicePayload {
  documentId: string;
  validatedFields: Record<string, string>;
  validationTimestamp: string;
  validatedBy: string;
  changesSummary: FieldChange[];
  auditTrail: {
    originalValues: Record<string, string>;
    confidenceScores: Record<string, number>;
    fieldTypes: Record<string, string>;
  };
}

/**
 * Transform validated data for Augmentum/Momentum system submission
 * Requirements: 5.5 - Transmit ValidatedData to Augmentum/Momentum system
 */
export const transformForAugmentum = (
  validatedData: ValidatedData,
  originalDocument: ExtractedDocumentData
): AugmentumPayload => {
  // Create confidence scores map
  const originalConfidenceScores: Record<string, number> = {};
  originalDocument.fields.forEach(field => {
    originalConfidenceScores[field.id] = field.confidence;
  });

  // Map field IDs to standard Augmentum field names
  const fieldMapping: Record<string, string> = {
    'grantor': 'grantor',
    'grantee': 'grantee',
    'parcel_number': 'parcelNumber',
    'consideration_amount': 'considerationAmount',
    'ownership_type': 'ownershipType',
    'legal_description': 'legalDescription',
    'recording_date': 'recordingDate',
    'document_id': 'documentId',
    'document_subtype': 'documentSubtype',
    'grantee_mailing_address': 'granteeMailingAddress',
    'sale_date': 'saleDate',
  };

  // Transform validated fields to Augmentum format
  const augmentumFields: AugmentumPayload['validatedFields'] = {};
  
  Object.entries(validatedData.validatedFields).forEach(([fieldId, value]) => {
    const augmentumFieldName = fieldMapping[fieldId] || fieldId;
    augmentumFields[augmentumFieldName] = value;
  });

  return {
    documentId: validatedData.documentId,
    documentType: originalDocument.metadata.documentType,
    validatedFields: augmentumFields,
    metadata: {
      validationTimestamp: validatedData.validationTimestamp,
      validatedBy: validatedData.validatedBy,
      originalConfidenceScores,
      changesSummary: validatedData.changesSummary,
    },
  };
};

/**
 * Transform validated data for internal validation service
 * Requirements: 5.4 - Store validated data for audit trail
 */
export const transformForValidationService = (
  validatedData: ValidatedData,
  originalDocument: ExtractedDocumentData
): ValidationServicePayload => {
  // Create audit trail data
  const originalValues: Record<string, string> = {};
  const confidenceScores: Record<string, number> = {};
  const fieldTypes: Record<string, string> = {};

  originalDocument.fields.forEach(field => {
    originalValues[field.id] = field.value;
    confidenceScores[field.id] = field.confidence;
    fieldTypes[field.id] = field.fieldType;
  });

  return {
    documentId: validatedData.documentId,
    validatedFields: validatedData.validatedFields,
    validationTimestamp: validatedData.validationTimestamp,
    validatedBy: validatedData.validatedBy,
    changesSummary: validatedData.changesSummary,
    auditTrail: {
      originalValues,
      confidenceScores,
      fieldTypes,
    },
  };
};

/**
 * Create field changes summary by comparing original and validated values
 * Requirements: 4.5 - Maintain edit history for audit trail
 */
export const createChangesSummary = (
  originalFields: ExtractedField[],
  validatedFields: Record<string, string>
): FieldChange[] => {
  const changes: FieldChange[] = [];

  originalFields.forEach(field => {
    const originalValue = field.value;
    const newValue = validatedFields[field.id] || '';

    // Only include changes where values actually differ
    if (originalValue !== newValue) {
      changes.push({
        fieldId: field.id,
        fieldName: field.name,
        originalValue,
        newValue,
        confidence: field.confidence,
      });
    }
  });

  return changes;
};

/**
 * Create ValidatedData object from form state
 * Requirements: 4.2, 4.5 - Store corrections as ValidatedData with edit history
 */
export const createValidatedData = (
  documentId: string,
  validatedFields: Record<string, string>,
  validatedBy: string,
  originalDocument: ExtractedDocumentData
): ValidatedData => {
  const changesSummary = createChangesSummary(originalDocument.fields, validatedFields);

  return {
    documentId,
    validatedFields,
    validationTimestamp: new Date().toISOString(),
    validatedBy,
    changesSummary,
  };
};

/**
 * Normalize field values based on field type
 * Requirements: Tech requirements - normalize dates to ISO 8601
 */
export const normalizeFieldValue = (value: string, fieldType: string): string => {
  if (!value || value.trim().length === 0) {
    return '';
  }

  switch (fieldType) {
    case 'date':
      // Normalize to ISO 8601 format (YYYY-MM-DD)
      try {
        const date = new Date(value.trim());
        if (!isNaN(date.getTime())) {
          return date.toISOString().split('T')[0];
        }
      } catch {
        // Return original value if parsing fails
      }
      return value.trim();

    case 'currency':
      // Normalize currency to standard decimal format
      try {
        const cleanedValue = value.trim().replace(/[$,\s]/g, '');
        const numericValue = parseFloat(cleanedValue);
        if (!isNaN(numericValue)) {
          return numericValue.toFixed(2);
        }
      } catch {
        // Return original value if parsing fails
      }
      return value.trim();

    case 'number':
      // Normalize numbers
      try {
        const numericValue = parseFloat(value.trim());
        if (!isNaN(numericValue)) {
          return numericValue.toString();
        }
      } catch {
        // Return original value if parsing fails
      }
      return value.trim();

    case 'text':
    default:
      // Trim whitespace for text fields
      return value.trim();
  }
};

/**
 * Normalize all field values in a validated fields object
 */
export const normalizeAllFieldValues = (
  validatedFields: Record<string, string>,
  originalFields: ExtractedField[]
): Record<string, string> => {
  const normalized: Record<string, string> = {};

  // Create a map of field types for quick lookup
  const fieldTypeMap = new Map<string, string>();
  originalFields.forEach(field => {
    fieldTypeMap.set(field.id, field.fieldType);
  });

  // Normalize each field value
  Object.entries(validatedFields).forEach(([fieldId, value]) => {
    const fieldType = fieldTypeMap.get(fieldId) || 'text';
    normalized[fieldId] = normalizeFieldValue(value, fieldType);
  });

  return normalized;
};

/**
 * Validate payload before API submission
 */
export const validatePayload = (payload: any): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!payload.documentId) {
    errors.push('Document ID is required');
  }

  if (!payload.validatedFields || Object.keys(payload.validatedFields).length === 0) {
    errors.push('At least one validated field is required');
  }

  if (!payload.validationTimestamp) {
    errors.push('Validation timestamp is required');
  }

  if (!payload.validatedBy) {
    errors.push('Validated by user is required');
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};